import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Scale, Timer, ShieldCheck } from "lucide-react";
import { BUILD } from "@/lib/build";

const CAPS = [
  { icon: Scale, title: "Yield & Allocation Tracking", body: "Monitor output per production line with itemised accuracy." },
  { icon: Timer, title: "Labour & Time Metrics", body: "Capture minutes, tasks, and throughput with precision." },
  { icon: ShieldCheck, title: "Shift Integrity", body: "Ensure balanced reconciliation across every shift, from weigh-in to sign-off." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState(null);
  useEffect(() => { api.get("/orgs").then((r) => setOrgs(r.data)).catch(() => setOrgs([])); }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="landing-page">
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/landing-hero.jpg)" }} aria-hidden />
        {/* desktop scrim (soft left third) */}
        <div className="absolute inset-0 hidden md:block" aria-hidden
          style={{ background: "linear-gradient(90deg, rgba(255,255,255,.94), rgba(255,255,255,.68) 45%, rgba(255,255,255,0) 75%)" }} />
        {/* mobile scrim (stronger, vertical) so text never fights the photo */}
        <div className="absolute inset-0 md:hidden" aria-hidden
          style={{ background: "linear-gradient(180deg, rgba(255,255,255,.96) 0%, rgba(255,255,255,.9) 48%, rgba(255,255,255,.55) 100%)" }} />
        <div className="relative px-6 py-14 md:py-28 max-w-6xl mx-auto">
          <div className="max-w-xl">
            <img src="/logo-horizontal.png" alt="Ready2Work AU — Precision Production Management Systems" data-testid="landing-logo" className="h-16 sm:h-20 w-auto" />
            <h1 className="mt-6 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900">Precision Production Management for Food Processing</h1>
            <p className="mt-4 text-lg text-slate-700 font-medium">Streamline your workforce planning and production data with Ready2Work AU.</p>
            <p className="mt-3 text-base text-slate-600 max-w-lg">Designed for the fast-paced demands of food processing, our platform delivers real-time tracking of labour allocation, yield, and shift performance—directly from the production floor.</p>
          </div>
        </div>
      </section>

      {/* KEY CAPABILITIES */}
      <section className="max-w-5xl mx-auto w-full px-6 py-10 grid gap-3 md:grid-cols-3">
        {CAPS.map((c) => (
          <div key={c.title} data-testid={`cap-${c.title.split(" ")[0].toLowerCase()}`} className="rounded-2xl border border-slate-100 bg-white shadow-sm p-5">
            <div className="w-11 h-11 rounded-xl btn-grad grid place-items-center text-white mb-3"><c.icon size={22} strokeWidth={2.4} /></div>
            <div className="font-heading font-bold text-slate-800">{c.title}</div>
            <p className="text-sm text-slate-600 mt-1">{c.body}</p>
          </div>
        ))}
      </section>

      <p className="max-w-5xl mx-auto w-full px-6 pb-8 text-center font-heading font-bold text-slate-800 text-lg">Optimise your workflow. Validate your data. Elevate your operational visibility.</p>

      {/* CHOOSE YOUR SYSTEM */}
      <section className="max-w-3xl mx-auto w-full px-6 pb-10">
        <div className="bg-slate-50 rounded-3xl border border-slate-100 p-5">
          <p className="text-slate-500 text-sm mb-4 font-semibold">Choose your system</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {orgs === null && <div className="text-slate-400 text-sm py-6">Loading systems…</div>}
            {orgs?.length === 0 && <div className="text-slate-400 text-sm py-6">No systems configured yet.</div>}
            {(orgs || []).map((o) => (
              <button key={o.slug} data-testid={`org-card-${o.slug}`} onClick={() => navigate(`/${o.slug}`)}
                className="text-left rounded-2xl border-2 border-slate-100 bg-white hover:border-teal-400 hover:bg-teal-50/40 active:scale-[0.99] transition-all p-4 flex items-center gap-4 min-h-[88px]">
                <span className="text-4xl leading-none">{o.emoji}</span>
                <div className="flex-1">
                  <div className="font-heading font-bold text-slate-800 text-lg">{o.name}</div>
                  <div className="text-xs text-slate-400">{o.tagline || "Production portal"}</div>
                </div>
                <ArrowRight className="text-teal-500" strokeWidth={2.5} />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="mt-auto border-t border-slate-100 py-6 text-center text-xs text-slate-400" data-testid="landing-footer">
        © Ready2Work AU · Production management &amp; performance
        <div data-testid="build-stamp" className="mt-1 text-[11px] text-slate-300 font-num">{BUILD}</div>
      </footer>
    </div>
  );
}
